import { useEffect, useState, useRef, useMemo } from 'react'
import * as Sentry from '@sentry/react'
import { serialize, deserialize } from './serializationUtils'
import { useDebouncedState } from '@hooks/useDebouncedState'
import debounce from 'debounce'

const saveToLocalStorage = debounce(
  <T extends object>(
    key: string,
    data: T,
    injectedLocalStorage = localStorage,
  ) => {
    injectedLocalStorage.setItem(
      key,
      serialize(data, {
        onError: (error) => {
          Sentry.captureException(error, {
            tags: {
              operation: 'localStorage_serialize',
              key: key,
            },
            extra: {
              valueType: data ? typeof data : 'undefined',
              isArray: Array.isArray(data),
              isMap: data instanceof Map,
              isSet: data instanceof Set,
            },
          })
        },
      }),
    )
  },
  500,
)

export const useLocalStorageState = <T extends object>(
  key: string,
  initialState: T,
  injectedLocalStorage = localStorage, // injected to make it easier to mock and test
) => {
  const [memoizedKey] = useState(key) // this is intentional, it's a workaround to references to a string changing on each rerender

  const { state, setState, debounced } = useDebouncedState<T>(initialState)

  const preventSave = useRef(true)

  useEffect(() => {
    preventSave.current = false // prevent saving until localstorage is loaded
    const value = injectedLocalStorage.getItem(memoizedKey)

    if (!value) return

    setState(
      deserialize<T>(value, {
        onError: (error) => {
          Sentry.captureException(error, {
            tags: {
              operation: 'localStorage_deserialize',
              key,
            },
            extra: {
              valueLength: value?.length || 0,
              valuePreview: value?.substring(0, 100),
            },
          })
        },
      }) ?? initialState,
    )
  }, [memoizedKey, injectedLocalStorage]) // eslint-disable-line react-hooks/exhaustive-deps
  /**
   * I'm ignoring the missing debouncedSetState and initialState
   * dependencies above because it does not actually change
   * We want this to only run when the component mounts, essentially
   */

  return useMemo(() => {
    const clear = () => {
      injectedLocalStorage.removeItem(memoizedKey)
      setState(initialState)
    }

    const recordState = (newState: T | ((prevState: T) => T)) => {
      setState((prev = initialState) => {
        const updatedState =
          typeof newState === 'function' ? newState(prev) : newState

        if (!preventSave.current) {
          saveToLocalStorage(memoizedKey, updatedState, injectedLocalStorage)
        }

        return updatedState
      })
    }

    const changedAttributes = new Set(
      Object.keys(initialState).filter((key) => {
        const initialValue = initialState[key as keyof T]
        const currentValue = state && state[key as keyof T]

        const normalizeValue = (value: unknown) =>
          value instanceof Set ? Array.from(value) : value

        return (
          JSON.stringify(normalizeValue(initialValue)) !==
          JSON.stringify(normalizeValue(currentValue))
        )
      }),
    )

    const isChanged = changedAttributes.size > 0

    return {
      state,
      debounced,
      setState: recordState,
      clear,
      changedAttributes,
      isChanged,
    }
  }, [
    state,
    debounced,
    setState,
    initialState,
    memoizedKey,
    injectedLocalStorage,
  ])
}
